import { Stack } from '@mui/material';
import { setUpTOTP } from 'aws-amplify/auth';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { MfaAppIcon } from '@/shared/assets/icons/mfa-app-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { Button } from '@/shared/components/ui/button/button';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { OverlayLoader } from '@/shared/components/ui/overlay-loader/overlay-loader';
import { QRCodeContainer } from '@/shared/features/rbac/components/enable-mfa/enable-mfa.style';
import {
  TEnableMfaProps,
  TMfaParameters,
} from '@/shared/features/rbac/components/enable-mfa/enable-mfa.types';
import { EnableMfaForm } from '@/shared/features/rbac/components/enable-mfa-form/enable-mfa-form';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import { showToastError } from '@/shared/utils/toast';

export const EnableMfa: React.FC<TEnableMfaProps> = ({ trigger, appUrl }) => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const [isLoadingParameters, startTransition] = useTransition();
  const [mfaParameters, setMfaParameters] = useState<
    TMfaParameters | undefined
  >();

  const getMfaSetup = async (
    _: React.BaseSyntheticEvent,
    closeDrawer?: () => void,
  ) => {
    startTransition(async () => {
      try {
        const { sharedSecret } = await setUpTOTP();
        setMfaParameters({
          url: `otpauth://totp/${appUrl}:${user?.attributes?.email}?secret=${sharedSecret}`,
          secret: sharedSecret,
        });
      } catch {
        showToastError(t('rbac:fetching_mfa_parameters_failed'));
        closeDrawer?.();
      }
    });
  };

  const displaySecretCode = () => {
    if (!mfaParameters?.secret) {
      return;
    }

    toast.info(mfaParameters?.secret, {
      autoClose: false,
      icon: <MfaAppIcon variant="info" />,
      onClick: () => {
        navigator.clipboard.writeText(mfaParameters.secret);
        toast.success(t('common:copied_to_clipboard'));
      },
    });
  };

  return (
    <DrawerController
      trigger={
        trigger ?? (
          <MenuItem icon={<MfaAppIcon />}>{t('rbac:enable_mfa')}</MenuItem>
        )
      }
      onTriggerClick={getMfaSetup}
      title={t('rbac:enable_mfa')}
      showDivider={false}
      onClose={() => setMfaParameters(undefined)}
    >
      {(handleClose) => {
        const onClose = () => {
          handleClose();
          setMfaParameters(undefined);
        };
        return (
          <Stack gap="2rem" height="100%" alignItems="center">
            <OverlayLoader display={isLoadingParameters} />
            {mfaParameters && (
              <>
                <Alert color="blue" text={t('rbac:enable_mfa_hint')} />
                <QRCodeContainer>
                  <QRCodeCanvas size={200} value={mfaParameters.url} />
                </QRCodeContainer>
                <Button variant="secondary" onClick={displaySecretCode}>
                  {t('rbac:display_code')}
                </Button>
                <EnableMfaForm onClose={onClose} />
              </>
            )}
          </Stack>
        );
      }}
    </DrawerController>
  );
};
